import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import { SocialMediaPostPreview } from './SocialMediaPostPreview';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { Brand } from '../../types';
import { useTranslation } from 'react-i18next';
import { InlineLoader } from '../common/InlineLoader';

interface PreviewCardProps {
  content: string;
  platforms?: SocialPlatform[];
  selectedPlatforms?: SocialPlatform[];
  media?: File | null;
  multiImages?: File[];
  existingMediaUrls?: string[];
  mediaUrl?: string | null;
  mediaType?: 'image' | 'video' | null;
  existingMediaUrl?: string | null;
  existingMediaType?: 'image' | 'video' | null;
  format?: 'regular' | 'reel';
  selectedBrand?: string;
  brandName?: string;
  brandLogo?: string;
  brands?: Brand[];
  hideEngagementCounts?: boolean;
}

export const PreviewCard: React.FC<PreviewCardProps> = ({
  content,
  platforms = [],
  selectedPlatforms = [],
  media = null,
  multiImages = [],
  existingMediaUrls = [],
  mediaUrl = null,
  mediaType = null,
  existingMediaUrl,
  existingMediaType,
  format = 'regular',
  selectedBrand,
  brandName,
  brandLogo,
  brands = [],
  hideEngagementCounts = false
}) => {
  const { t } = useTranslation();
  const brandObject = brands.find(brand => brand.id === selectedBrand);
  
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [multiObjectUrls, setMultiObjectUrls] = useState<string[]>([]);
  const [activePreviewPlatform, setActivePreviewPlatform] = useState<SocialPlatform | null>(null);
  const [pdfThumbUrl, setPdfThumbUrl] = useState<string | null>(null);
  const [isGeneratingThumb, setIsGeneratingThumb] = useState<boolean>(false);
  
  useEffect(() => {
    if (media) {
      const url = URL.createObjectURL(media);
      setObjectUrl(url);

      // Preload the image to ensure it's ready for display
      if (media.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          // Image is loaded and ready - force re-render
          setObjectUrl(prev => prev === url ? url + '?loaded' : url);
        };
        img.onerror = () => {
          // If loading fails, still show the image
          setObjectUrl(url);
        };
        img.src = url;
      }

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setObjectUrl(null);
    }
    return undefined;
  }, [media]);

  // Build object URLs for multi-images
  useEffect(() => {
    if (multiImages && multiImages.length > 0) {
      const urls = multiImages.map((f) => URL.createObjectURL(f));
      setMultiObjectUrls(urls);
      return () => {
        urls.forEach((u) => URL.revokeObjectURL(u));
      };
    }
    // No file-based multi images; ensure we clear object URLs and rely on existing URL list
    setMultiObjectUrls([]);
    return undefined;
  }, [multiImages]);
  
  const mediaObject = media 
    ? {
        type: media.type.startsWith('image/') ? 'image' as const : 'video' as const,
        url: objectUrl || '',
        uploadedAt: new Date().toISOString()
      } 
    : mediaUrl || existingMediaUrl 
      ? {
          type: (mediaType || existingMediaType || 'image') as 'image' | 'video',
          url: mediaUrl || existingMediaUrl || '',
          uploadedAt: new Date().toISOString()
        } 
      : undefined;

  // Detect PDF source (file or URL)
  const isPdfFile = Boolean(media && ((media.type === 'application/pdf') || media.name?.toLowerCase().endsWith('.pdf')));
  const urlCandidate = mediaUrl || existingMediaUrl || '';
  const isPdfUrl = Boolean(!isPdfFile && typeof urlCandidate === 'string' && urlCandidate.toLowerCase().includes('.pdf'));

  // Generate first-page thumbnail for PDFs
  useEffect(() => {
    let cancelled = false;
    async function loadPdfJs() {
      const w = window as any;
      if (w.pdfjsLib) return w.pdfjsLib;
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('pdfjs load failed'));
        document.head.appendChild(s);
      });
      const lib = (window as any).pdfjsLib;
      if (lib) lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
      return lib;
    }

    async function generate() {
      try {
        if (!isPdfFile && !isPdfUrl) {
          setPdfThumbUrl(null);
          return;
        }
        setIsGeneratingThumb(true);
        const pdfjsLib = await loadPdfJs();
        let loadingTask: any;
        if (isPdfFile && media) {
          const buf = await media.arrayBuffer();
          loadingTask = pdfjsLib.getDocument({ data: buf });
        } else if (isPdfUrl) {
          const effectiveUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? `/api/pdf-proxy?url=${encodeURIComponent(urlCandidate)}`
            : urlCandidate;
          const resp = await fetch(effectiveUrl);
          const buf = await resp.arrayBuffer();
          loadingTask = pdfjsLib.getDocument({ data: buf });
        } else {
          return;
        }
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const baseViewport = page.getViewport({ scale: 1 });
        // 4:5 portrait frame (Instagram-friendly)
        const frameWidth = 1080;
        const frameHeight = 1350; // 1080 * 5 / 4
        const fitScale = Math.min(frameWidth / baseViewport.width, frameHeight / baseViewport.height);
        const scaledViewport = page.getViewport({ scale: fitScale });

        // Render page onto a temporary canvas at scaled size
        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d');
        if (!pageCtx) return;
        pageCanvas.width = Math.floor(scaledViewport.width);
        pageCanvas.height = Math.floor(scaledViewport.height);
        await page.render({ canvasContext: pageCtx, viewport: scaledViewport }).promise;

        // Composite into 4:5 frame with white background and centered page
        const frameCanvas = document.createElement('canvas');
        const fctx = frameCanvas.getContext('2d');
        if (!fctx) return;
        frameCanvas.width = frameWidth;
        frameCanvas.height = frameHeight;
        fctx.fillStyle = '#ffffff';
        fctx.fillRect(0, 0, frameWidth, frameHeight);
        const offsetX = Math.floor((frameWidth - pageCanvas.width) / 2);
        const offsetY = Math.floor((frameHeight - pageCanvas.height) / 2);
        fctx.drawImage(pageCanvas, offsetX, offsetY);
        const dataUrl = frameCanvas.toDataURL('image/png');
        if (!cancelled) setPdfThumbUrl(dataUrl);
      } catch (e) {
        if (!cancelled) setPdfThumbUrl(null);
      } finally {
        if (!cancelled) setIsGeneratingThumb(false);
      }
    }
    generate();
    return () => { cancelled = true; };
  }, [media, mediaUrl, existingMediaUrl, isPdfFile, isPdfUrl]);

  // Final effective media for preview
  const effectiveMedia = (() => {
    if ((isPdfFile || isPdfUrl) && pdfThumbUrl) {
      // Mark as PDF thumbnail so downstream previews can adapt aspect ratio (4:5)
      const markedUrl = `${pdfThumbUrl}#pdfthumb`;
      return { type: 'image' as const, url: markedUrl, uploadedAt: new Date().toISOString() };
    }
    return mediaObject;
  })();
  
  const displayPlatforms = platforms.length > 0 ? platforms : selectedPlatforms;
  
  useEffect(() => {
    // If there are platforms to display
    if (displayPlatforms.length > 0) {
      // If there's no active platform OR the current active one is no longer in the list
      if (!activePreviewPlatform || !displayPlatforms.includes(activePreviewPlatform)) {
        setActivePreviewPlatform(displayPlatforms[0]); // Set to the first available one
      }
    } else {
      // If there are no platforms to display, clear the active platform
      setActivePreviewPlatform(null);
    }
  }, [displayPlatforms, activePreviewPlatform]); // Added activePreviewPlatform to dependency array for robustness, though displayPlatforms is the primary trigger
  
  const getTranslatedPlatformName = (platform: SocialPlatform): string => {
    const key = `platforms.${platform}`;
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4">
      <div className="flex items-center gap-3 mb-4">
        <Eye className="w-5 h-5 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">{t('cards.preview.title', 'Preview')}</h2>
      </div>
      
      {displayPlatforms.length > 0 ? (
        <>
          <div className="flex space-x-1 mb-4 border-b border-gray-200 overflow-x-auto">
            {displayPlatforms.map(platform => (
              <button
                key={platform}
                onClick={() => setActivePreviewPlatform(platform)}
                className={`py-2 px-3 flex items-center gap-2 whitespace-nowrap ${
                  activePreviewPlatform === platform
                    ? 'border-b-2 border-primary-600 text-primary-600 font-medium'
                    : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                }`}
                aria-pressed={activePreviewPlatform === platform}
              >
                <SocialIcon platform={platform} size={18} />
              </button>
            ))}
          </div>

          {activePreviewPlatform ? (
            <div className="rounded-lg p-0">
              {/* The platform name is now part of the tab, so we might not need it duplicated here unless desired */}
              {/* <div className="flex items-center gap-2 mb-3">
                <SocialIcon platform={activePreviewPlatform} size={20} />
                <span className="text-sm font-medium text-gray-700">
                  {t('cards.preview.platformPreviewTitle', '{{platformName}} Preview', {
                    platformName: getTranslatedPlatformName(activePreviewPlatform)
                  })}
                </span>
              </div> */}
              
              {(isPdfFile || isPdfUrl) && !pdfThumbUrl ? (
                <div className="flex items-center justify-center h-[360px]">
                  <InlineLoader message={t('common.loading', 'Loading...')} />
                </div>
              ) : (
                <SocialMediaPostPreview
                  platform={activePreviewPlatform}
                  content={content}
                  media={effectiveMedia}
                  mediaUrl={
                    objectUrl ||
                    (multiObjectUrls.length === 1 ? multiObjectUrls[0] : undefined) ||
                    (existingMediaUrls && existingMediaUrls.length === 1 ? existingMediaUrls[0] : undefined)
                  }
                  mediaUrls={
                    multiObjectUrls.length > 0
                      ? multiObjectUrls
                      : (existingMediaUrls && existingMediaUrls.length > 0 ? existingMediaUrls : undefined)
                  }
                  brand={brandObject}
                  brandName={brandName || brandObject?.name}
                  brandLogo={brandLogo || brandObject?.logo}
                  format={format}
                  hideEngagementCounts={hideEngagementCounts}
                />
              )}
            </div>
          ) : (
             // This case should ideally not be reached if displayPlatforms.length > 0 and activePreviewPlatform is set in useEffect
            <div className="text-center py-8 text-gray-500">
              {t('cards.preview.selectPlatformPrompt', 'Select a platform to see the preview')}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {t('cards.preview.selectPlatformPrompt', 'Select a platform to see the preview')}
        </div>
      )}
    </div>
  );
};